import React, { ReactNode, useCallback } from 'react';
import { useAttachmentContext } from './AttachmentContext';
import { useBreakpoint } from './useBreakpoint';
import type { Attachment } from '@/hooks/useChatSession';

interface ChartAttachableProps {
  id: string;
  title: string;
  /**
   * Snapshotted at click-time so the attachment reflects the chart's current
   * filters (selected athletes, visible period, aggregated data).
   */
  getDataSlice: () => Pick<Attachment, 'period' | 'athletes' | 'data'>;
  children: ReactNode;
  className?: string;
  /** Optional `data-tour` anchor slug for guided tutorials. */
  dataTour?: string;
}

export const ChartAttachable: React.FC<ChartAttachableProps> = ({
  id, title, getDataSlice, children, className = '', dataTour,
}) => {
  const { drawerOpen, stageAttachment, isStaged } = useAttachmentContext();
  const bp = useBreakpoint();

  const handleClick = useCallback(() => {
    if (!drawerOpen) return;
    const slice = getDataSlice();
    stageAttachment({
      chart_id: id,
      title,
      period: slice.period,
      athletes: slice.athletes,
      data: slice.data,
    });
  }, [drawerOpen, getDataSlice, id, title, stageAttachment]);

  const attachable = drawerOpen;
  const staged = isStaged(id);

  const dynamicClass = [
    className,
    attachable && bp === 'desktop'
      ? 'cursor-pointer outline-1 outline-dashed outline-transparent hover:outline-white/40'
      : '',
    attachable && bp === 'mobile'
      ? 'outline-1 outline-dashed outline-white/30'
      : '',
    staged ? 'outline-2 outline-solid outline-white/70' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      role={attachable ? 'button' : undefined}
      tabIndex={attachable ? 0 : undefined}
      aria-pressed={attachable ? staged : undefined}
      data-tour={dataTour}
      className={dynamicClass}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (attachable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {children}
    </div>
  );
};
